import { useState } from 'react';
import { Button, Modal, Paper, Stack, Text } from '@mantine/core';
import { columnTypeText, MULTI_CHOICE_NOTE } from './columnText';
import { ChoiceButton } from './ChoiceButton';
import { useSetup } from '../../SetupContext';

/** Two steps: pick a column, then No overlap or Keep together. */
export function AddRequirementModal({ onClose }: { onClose: () => void }) {
  const { session, config, update } = useSetup();
  const [columnKey, setColumnKey] = useState<string | null>(null);
  const column = session.columns.find(c => c.key === columnKey);
  const ruleCols = new Set(config.columnRules.map(r => r.column));
  const goalCols = new Set(config.goals.map(g => g.column));
  const options = session.columns.filter(c => !ruleCols.has(c.key));
  const label = column?.label.toLowerCase() ?? 'value';
  const isMulti = column?.kind === 'multi';

  const add = (ruleType: 'no_overlap' | 'keep_together') => {
    if (!column) return;
    update({ type: 'addColumnRule', column: column.key, ruleType });
    onClose();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title="Add a group requirement"
      size={560}
      centered
    >
      {!column ? (
        <Stack gap="xs">
          <Text size="sm" c="dimmed" mb="xs">
            Step 1 of 2. Pick a column. Matchings that break a group requirement
            are never shown.
          </Text>
          {options.length === 0 && (
            <Paper bg="gray.0" p="md" radius="md">
              <Text size="sm">
                Every column already has a group requirement.
              </Text>
            </Paper>
          )}
          {options.map(c => (
            <ChoiceButton
              key={c.key}
              label={c.label}
              description={`${columnTypeText(c)}${goalCols.has(c.key) ? '. This column is also a matching goal.' : ''}`}
              onClick={() => setColumnKey(c.key)}
            />
          ))}
        </Stack>
      ) : (
        <Stack gap="sm">
          <Text size="sm" c="dimmed" mb="xs">
            Step 2 of 2. What should the requirement do for {column.label}?
          </Text>
          <ChoiceButton
            label="No overlap"
            description={`No two people in a group share a value in this column. For example, no two people from the same ${label}.`}
            onClick={() => add('no_overlap')}
          />
          <ChoiceButton
            label="Keep together"
            disabled={isMulti}
            description={
              isMulti
                ? MULTI_CHOICE_NOTE
                : `Everyone in a group shares the same value in this column. For example, everyone from the same ${label}.`
            }
            onClick={() => add('keep_together')}
          />
          <Button
            variant="default"
            mt="xs"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setColumnKey(null)}
          >
            Back
          </Button>
        </Stack>
      )}
    </Modal>
  );
}
